// LivePROTicker — Home R₿L section · rotates the latest PRO members and a
// total counter. Sources /api/pro/recent-members (returns members + total).
// Refreshes every 60s, rotates every 4s. Never shows a static placeholder.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { API } from "../../lib/api";

const T = {
  es: {
    live: "EN VIVO",
    joined: "se unió al PRO",
    joined_rbl: "— Miembro PRO con R₿L",
    counter: (n) => `${n} Rebeldes PRO y creciendo`,
    loading: "Cargando rebeldes PRO…",
  },
  en: {
    live: "LIVE",
    joined: "joined PRO",
    joined_rbl: "— PRO Member with R₿L",
    counter: (n) => `${n} PRO Rebels and growing`,
    loading: "Loading PRO rebels…",
  },
  sk: {
    live: "NAŽIVO",
    joined: "sa pridal k PRO",
    joined_rbl: "— PRO člen s R₿L",
    counter: (n) => `${n} PRO Rebelov a rastie`,
    loading: "Načítavanie PRO rebelov…",
  }, de: { live: "LIVE", joined: "ist PRO beigetreten", joined_rbl: "— PRO-Mitglied mit R₿L", counter: (n) => `${n} PRO-Rebellen und wachsend`, loading: "Lade PRO-Rebellen…" }, "zh-CN": { live: "直播", joined: "加入了 PRO", joined_rbl: "— PRO 会员持有 R₿L", counter: (n) => `${n} 名 PRO 反叛者且持续增长`, loading: "加载 PRO 会员中…" },
};

const fmt = (n) => Number(n || 0).toLocaleString("es-ES");

const LivePROTicker = () => {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [idx, setIdx] = useState(0);
  const fetchRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/pro/recent-members?limit=5`);
      if (!r.ok) return;
      const d = await r.json();
      setMembers(Array.isArray(d.members) ? d.members : []);
      setTotal(Number(d.total || 0));
    } catch { /* silent */ }
  }, []);

  // initial fetch + refresh every 60s
  useEffect(() => {
    load();
    fetchRef.current = setInterval(load, 60_000);
    return () => clearInterval(fetchRef.current);
  }, [load]);

  // Build rotating message list: [...members, counter]
  const messages = [];
  for (const m of members) {
    messages.push({ kind: "member", member: m });
  }
  if (total > 0) {
    messages.push({ kind: "counter", total });
  }

  useEffect(() => {
    if (messages.length < 2) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % messages.length), 4000);
    return () => clearInterval(id);
  }, [messages.length]);

  if (messages.length === 0) {
    return (
      <div
        className="mx-auto mt-2 flex max-w-2xl items-center gap-2 rounded-full border border-amber-400/20 bg-black/40 px-4 py-1.5 text-[11px] text-white/55 backdrop-blur"
        data-testid="live-pro-ticker-loading"
      >
        <Crown className="h-3 w-3 text-amber-300" /> {t.loading}
      </div>
    );
  }

  const cur = messages[idx % messages.length];
  const isRbl = cur.kind === "member" && (cur.member.via === "rbl" || cur.member.method === "rbl_token");
  const tail = cur.kind === "member" ? (isRbl ? t.joined_rbl : t.joined) : null;

  return (
    <div
      className="relative mx-auto flex max-w-2xl items-center gap-3 rounded-full border border-amber-400/30 bg-gradient-to-r from-amber-500/10 via-black/60 to-violet-500/10 px-4 py-1.5 backdrop-blur"
      data-testid="live-pro-ticker"
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
      </span>
      <span className="hidden font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300 sm:inline">
        {t.live}
      </span>
      <div className="min-w-0 flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${cur.kind}-${(cur.member && cur.member.user) || "counter"}-${idx}`}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-2 truncate text-xs text-white/90"
          >
            <Crown className="h-3.5 w-3.5 shrink-0 text-amber-300" />
            {cur.kind === "counter" ? (
              <span className="truncate font-bold text-amber-200" data-testid="ticker-pro-counter">
                👑 {t.counter(fmt(cur.total))}
              </span>
            ) : (
              <span className="truncate" data-testid="ticker-pro-member">
                👑 <span className="font-mono text-white">{cur.member.user}</span>{" "}
                <span className={isRbl ? "font-bold text-amber-200" : "text-white/80"}>{tail}</span>
              </span>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LivePROTicker;
export { LivePROTicker };
